import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { FormManagementWorkspaceData } from '@/contracts/form-management'
import { Building2, CheckCircle2, ClipboardList, GitBranch, Lock, Users } from 'lucide-react'

type FormManagementCustomerWorkflowProps = {
  workspaceData: FormManagementWorkspaceData
  canManageForms: boolean
}

const groupLabels = {
  workflow: '流程控制',
  company: '公司資料',
  contact: '聯絡人',
  payment: '付款與規模',
  address: '地址資訊',
  receiving: '帳務與收貨',
  sales: '業務欄位',
  classification: '客戶分類',
  region: '區域資訊',
  notes: '其他欄位',
  credit: '授信欄位',
} as const

const stepStateLabels = {
  ready: '可預覽',
  mock_only: 'Mock flow',
  not_configured: '未接線',
} as const

const stepStateVariants = {
  ready: 'success',
  mock_only: 'secondary',
  not_configured: 'warning',
} as const

export function FormManagementCustomerWorkflow({
  workspaceData,
  canManageForms,
}: FormManagementCustomerWorkflowProps) {
  const workflow = workspaceData.customerWorkflow

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge>Mock-safe</Badge>
        <Badge variant={workflow.enabled ? 'success' : 'secondary'}>
          {workflow.enabled ? 'B2B customer onboarding' : '非 B2B 問卷'}
        </Badge>
        {!canManageForms && (
          <Badge variant="secondary" className="gap-1">
            <Lock className="size-3" />
            只讀
          </Badge>
        )}
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">待審核申請</span>
              <ClipboardList className="size-4 text-indigo-600" />
            </div>
            <div className="mt-3 text-2xl font-semibold text-slate-950">
              {workflow.applicationsPending}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">已更新客戶資料</span>
              <Building2 className="size-4 text-emerald-600" />
            </div>
            <div className="mt-3 text-2xl font-semibold text-slate-950">
              {workflow.customersCreated}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">ERP 建檔狀態</span>
              <Users className="size-4 text-amber-600" />
            </div>
            <div className="mt-3 text-lg font-semibold text-slate-950">
              {workflow.erpFilingStatus === 'filed' ? 'ERP已建檔' : 'ERP未建檔'}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-2">
              <GitBranch className="size-5 text-indigo-600" />
              <h3 className="font-semibold text-slate-950">建檔流程</h3>
            </div>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              這裡顯示 B2B 客戶建檔問卷的主管審核、欄位對應與 ERP 建檔摘要。資料仍是 synthetic
              workflow，不會建立真客戶資料。
            </p>

            <div className="mt-5 space-y-3">
              {workflow.reviewSteps.map((step, index) => (
                <div
                  key={step.id}
                  className="flex gap-3 rounded-md border border-slate-200 bg-white p-3"
                >
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-sm font-semibold text-indigo-700">
                    {index + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="font-medium text-slate-950">{step.label}</div>
                      <Badge variant={stepStateVariants[step.state]}>
                        {stepStateLabels[step.state]}
                      </Badge>
                    </div>
                    <p className="mt-1 text-sm text-slate-500">負責：{step.owner}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <h3 className="font-semibold text-slate-950">流程設定摘要</h3>
            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2">
                <span className="text-sm text-slate-700">主管審核</span>
                <Badge variant={workflow.requiresManagerReview ? 'success' : 'secondary'}>
                  {workflow.requiresManagerReview ? '需要' : '不需要'}
                </Badge>
              </div>
              <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2">
                <span className="text-sm text-slate-700">核准後自動建立客戶</span>
                <Badge variant={workflow.autoCreateCustomer ? 'success' : 'secondary'}>
                  {workflow.autoCreateCustomer ? '啟用' : '未啟用'}
                </Badge>
              </div>
              <div className="rounded-md bg-slate-50 px-3 py-2">
                <div className="text-sm font-medium text-slate-700">相關入口</div>
                <div className="mt-3 grid gap-2">
                  <Button type="button" variant="outline" disabled={!canManageForms}>
                    前往審核中心
                  </Button>
                  <Button type="button" variant="outline" disabled={!canManageForms}>
                    開啟欄位對應
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="size-5 text-emerald-600" />
            <h3 className="font-semibold text-slate-950">欄位對應 preview</h3>
          </div>
          <div className="mt-4 overflow-hidden rounded-md border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>問卷題目 / 來源</TableHead>
                  <TableHead>目標欄位</TableHead>
                  <TableHead>分類</TableHead>
                  <TableHead>必填</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {workflow.fieldMappings.map((mapping) => (
                  <TableRow key={mapping.id}>
                    <TableCell className="font-medium text-slate-900">
                      {mapping.sourceQuestion}
                    </TableCell>
                    <TableCell>{mapping.targetField}</TableCell>
                    <TableCell>{groupLabels[mapping.group]}</TableCell>
                    <TableCell>
                      <Badge variant={mapping.required ? 'success' : 'secondary'}>
                        {mapping.required ? '必填' : '選填'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
